<template>
  <main>
    <!-- Hero Section -->
    <section
      class="bg-depth-0/50 bg-[url('https://ccdn.g-portal.com/BG_Header_Tickets_2_c3de34b704.jpg')] bg-cover bg-right"
      aria-label="Support hero"
    >
      <div
        class="grid max-w-screen-xl px-4 pt-8 pb-0 mx-auto lg:gap-8 xl:gap-0 lg:pt-16 lg:grid-cols-12"
      >
        <!-- Left Hero -->
        <div class="mr-auto place-self-center lg:col-span-7">
          <h1
            class="uppercase max-w-2xl mb-4 text-4xl font-extrabold tracking-tight leading-none md:text-5xl xl:text-6xl text-mouse"
          >
            {{ $t('support/hero/title') }}
          </h1>
          <p
            class="max-w-2xl mb-6 font-light lg:mb-8 md:text-lg lg:text-xl text-mouse"
          >
            {{ $t('support/hero/subtitle') }}
          </p>

          <!-- Search -->
          <div class="relative w-full">
            <div
              class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none"
              aria-hidden="true"
            >
              <fa-icon :icon="faMagnifyingGlass" class="size-4 text-rhino" />
            </div>
            <input
              id="voice-search"
              :key="$i18n.locale"
              v-model="searchState.query"
              type="text"
              data-testid="support-search"
              class="text-sm rounded-md block w-full ps-10 py-2 bg-depth-0 outline-depth-3 placeholder-rhino text-mouse focus:outline-brand"
              :placeholder="$t('support/hero/search_placeholder')"
              autocomplete="off"
              role="searchbox"
              :aria-label="$t('support/hero/search_placeholder')"
            />
          </div>

          <div class="mt-4 space-y-2 min-h-8">
            <p v-if="searchState.loading" class="text-mouse">
              {{ $t('support/hero/status_searching') }}
            </p>
            <p v-else-if="searchState.error" class="text-danger">
              {{ $t('support/hero/status_error') }} {{ searchState.error }}
            </p>
            <p
              v-else-if="searchState.query && !hasSearchResults"
              class="text-mouse"
            >
              {{ $t('support/hero/status_no_results') }}
            </p>
          </div>
        </div>

        <!-- Right Hero -->
        <div
          class="hidden lg:mt-0 lg:col-span-5 lg:flex max-h-56 aspect-square mx-auto hover:animate-ghost"
        >
          <img
            src="https://ccdn.g-portal.com/Mr_M_Search_39fb64d2ef.svg"
            alt="Mr. Maintenance searching"
          />
        </div>
      </div>

      <!-- Search results -->
      <div
        v-if="hasSearchResults"
        class="max-w-screen-xl px-4 pb-8 pt-2 mx-auto"
      >
        <div class="bg-depth-1 rounded-md p-4 shadow-lg">
          <h3 class="text-lg font-semibold text-mouse mb-4">
            {{ $t('support/hero/search_result_wiki') }}
          </h3>

          <div
            v-if="searchState.data?.wiki?.length"
            class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2"
          >
            <div
              v-for="(item, i) in searchState.data.wiki"
              :key="`w-${i}-${item.link}`"
              class="rounded-md transition duration-150 cursor-pointer"
            >
              <a
                :href="item.link"
                target="_blank"
                rel="noopener"
                class="text-brand no-underline hover:underline focus:no-underline"
              >
                {{ item.title }}
              </a>
            </div>
          </div>

          <p v-else class="text-rhino">
            {{ $t('support/hero/search_result_empty') }}
          </p>
        </div>
      </div>

      <!-- Knowledge and FAQ -->
      <div
        v-if="KNOWLEDGE_SECTION_ENABLED"
        class="max-w-screen-xl px-4 pb-8 pt-4 mx-auto lg:pb-16 lg:pt-0"
      >
        <h2 class="text-2xl md:text-3xl font-bold text-mouse uppercase">
          {{ $t('support/knowledge/title') }}
        </h2>

        <!-- MOBILE: horizontal scrollable categories -->
        <div class="mt-3 md:hidden">
          <div
            class="no-scrollbar flex gap-3 overflow-x-auto snap-x snap-mandatory py-1 -mx-4 px-4"
            style="-webkit-overflow-scrolling: touch"
          >
            <button
              v-for="card in WIKI_CARDS"
              :key="card.title"
              type="button"
              class="shrink-0 snap-start rounded-lg px-4 py-3 flex items-center gap-2 border transition duration-200 bg-depth-2/85 border-depth-3 data-[active=true]:border-brand data-[active=true]:ring-1 data-[active=true]:ring-brand"
              :data-active="selectedWiki === card.title"
              :aria-pressed="selectedWiki === card.title"
              @click="selectedWiki = card.title"
            >
              <fa-icon :icon="card.icon" class="size-5 icon-gradient" />
              <span class="text-mouse text-sm font-semibold whitespace-nowrap">
                {{ card.title }}
              </span>
            </button>
          </div>

          <div class="mt-4 rounded-lg p-4 bg-depth-2/85">
            <h3 class="!text-lg !font-semibold !text-mouse mb-2">
              {{ selectedWiki }}
            </h3>
            <ul class="list-none p-0 m-0 [&>li+li]:mt-2">
              <li v-for="link in selectedWikiLinks" :key="link.title">
                <a
                  :href="link.link"
                  target="_blank"
                  rel="noopener"
                  class="text-brand no-underline focus:no-underline"
                >
                  {{ link.title }}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <!-- DESKTOP: grid -->
        <div
          class="mt-4 hidden md:grid grid-cols-1 md:grid-cols-2 gap-4 lg:grid-cols-3"
        >
          <div
            v-for="card in WIKI_CARDS"
            :key="card.title"
            class="rounded-lg p-4 shadow-sm bg-depth-2/85 md:p-6"
          >
            <div class="items-center space-x-0 flex gap-2 md:gap-4">
              <fa-icon
                :icon="card.icon"
                class="size-6 md:size-8 icon-gradient"
              />
              <h3 class="text-lg md:text-xl font-bold text-mouse">
                {{ card.title }}
              </h3>
            </div>

            <div class="mt-2 md:mt-4">
              <p
                v-for="link in card.links"
                :key="link.title"
                class="text-brand py-1"
              >
                <a :href="link.link" target="_blank" rel="noopener">
                  {{ link.title }}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Nav Buttons -->
    <section class="max-w-screen-xl px-4 py-8 mx-auto xl:gap-0 lg:py-16">
      <h2 class="text-2xl md:text-3xl font-bold text-mouse uppercase">
        {{ $t('support/nav/title') }}
      </h2>
      <p class="mb-6 font-light lg:mb-8 md:text-lg lg:text-xl text-mouse">
        {{ $t('support/nav/subtitle') }}
      </p>

      <div class="mt-4 grid grid-cols-2 gap-4 xl:grid-cols-4">
        <button
          v-for="btn in NAV_BUTTONS"
          :key="btn.key"
          type="button"
          :data-testid="`support-nav-${btn.key}`"
          class="items-center bg-depth-2 rounded-md px-2 py-4 md:px-4 md:py-6 shadow-sm flex gap-2 md:gap-4 hover:outline hover:outline-brand cursor-pointer"
          @click="handleCta(btn)"
        >
          <fa-icon :icon="btn.icon" class="size-6 md:size-8 icon-gradient" />

          <div class="text-left">
            <h3 class="md:text-xl font-bold m-0">
              {{ $t(`support/nav/${btn.key}_title`) }}
            </h3>
            <p class="text-rhino text-sm hidden md:block">
              {{ $t(`support/nav/${btn.key}_subtitle`) }}
            </p>
          </div>
        </button>
      </div>
    </section>

    <!-- Tickets -->
    <section id="tickets" class="mb-16">
      <!-- Ticket Hero -->
      <div
        class="min-h-[35rem] bg-depth-1/50 bg-[url('https://ccdn.g-portal.com/support_ticket_bg_2b6ad96c34.png')] bg-cover bg-center bg-blend-darken"
      >
        <div class="py-8 lg:py-16 px-4 mx-auto max-w-screen-xl">
          <h2
            class="uppercase mb-4 text-4xl tracking-tight font-extrabold text-center text-mouse"
          >
            {{
              panel === 'write'
                ? $t('support/create_ticket/title')
                : $t('support/ticket_table/title')
            }}
          </h2>
          <p
            v-if="panel === 'write'"
            class="mb-8 lg:mb-16 font-light text-center text-mouse sm:text-xl"
          >
            {{ $t('support/create_ticket/subtitle') }}
          </p>
        </div>
      </div>

      <!-- Tickets Card -->
      <div class="px-4" :class="panel === 'write' ? '-mt-80' : '-mt-96'">
        <div
          class="mx-auto bg-depth-2 rounded-lg shadow"
          :class="
            panel === 'table'
              ? 'p-0 max-w-screen-xl'
              : 'p-4 md:p-8 max-w-screen-lg'
          "
        >
          <!-- Unauthenticated: prompt to login -->
          <div v-if="!isAuth" class="text-center py-16 px-4">
            <p class="text-mouse mb-6">
              <i18n-t keypath="support/create_ticket/unauth">
                <template #login>
                  <button
                    type="button"
                    class="text-brand font-bold no-underline hover:underline"
                    @click="login()"
                  >
                    {{ $t('support/create_ticket/login') }}
                  </button>
                </template>
              </i18n-t>
            </p>
            <img
              src="https://ccdn.g-portal.com/Outlines_4x_6429110cd7.png"
              alt="Mr. Maintenance login"
              class="mx-auto max-h-48 animate-ghost"
            />
          </div>

          <form
            v-else-if="panel === 'write'"
            class="space-y-8"
            @submit.prevent="submitTicket"
          >
            <gp-input
              v-if="!isAuth"
              :label="$t('support/create_ticket/email_label')"
              :hint="$t('support/create_ticket/email_hint')"
              input-id="email"
              required
            >
              <input
                id="email"
                v-model="ticketForm.email"
                type="email"
                class="p-2 w-full border-b border-rhino/50 placeholder-rhino text-mouse focus:outline-none"
                :placeholder="$t('support/create_ticket/email_placeholder')"
                required
              />
            </gp-input>

            <!-- Game/Category -->
            <div class="grid gap-4 sm:grid-cols-2">
              <gp-input
                :label="$t('support/create_ticket/category_label')"
                :hint="$t('support/create_ticket/category_hint')"
                input-id="category"
              >
                <category-select
                  id="category"
                  v-model="ticketForm.category"
                  :general="support.uncategorizedCategories"
                  :games="support.gameCategories"
                  name="category"
                />
              </gp-input>

              <gp-input
                v-if="showServer"
                :label="$t('support/create_ticket/server_label')"
                :hint="$t('support/create_ticket/server_hint')"
                input-id="server"
              >
                <gp-select
                  v-model="ticketForm.server"
                  :items="filteredServers"
                  name="server"
                  :get-key="(s) => (s as UserServer).id"
                  :get-label="(s) => (s as UserServer).name"
                  :get-secondary="(s) => (s as UserServer).serverAddress || ''"
                  searchable
                  test-id="support-server"
                />
              </gp-input>
            </div>

            <!-- Subject -->
            <gp-input
              :label="$t('support/create_ticket/subject_label')"
              :hint="$t('support/create_ticket/subject_hint')"
              input-id="subject"
              required
            >
              <input
                id="subject"
                v-model="ticketForm.title"
                type="text"
                class="p-2 w-full border-b border-rhino/50 placeholder-rhino text-mouse focus:outline-none"
                required
              />
            </gp-input>

            <!-- Message -->
            <gp-input
              :label="$t('support/create_ticket/message_label')"
              :hint="$t('support/create_ticket/message_hint')"
              input-id="message"
              required
              is-textarea
            >
              <textarea
                id="message"
                v-model="ticketForm.message"
                rows="4"
                class="p-2 w-full border-b border-rhino/50 placeholder-rhino text-mouse focus:outline-none"
                required
              ></textarea>
            </gp-input>

            <!-- Addons -->
            <div v-if="showAddons">
              <p class="mb-2 font-bold text-mouse flex items-center gap-2">
                {{ $t('support/addons/title') }}
                <a
                  :href="supportOptionsWikiUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open G-Portal upgrade information"
                >
                  <fa-icon :icon="faInfoCircle" class="size-4 text-rhino" />
                </a>
              </p>

              <ul class="grid w-full gap-6 md:grid-cols-3">
                <li
                  v-for="upgrade in filteredUpgradeOptions"
                  :key="upgrade.type"
                  class="relative h-full"
                >
                  <input
                    :id="`upgrade-${upgrade.type}`"
                    v-model="ticketForm.upgrade"
                    type="radio"
                    name="upgrade"
                    :value="upgrade.type"
                    class="hidden peer"
                    :disabled="!upgrade.enabled"
                  />

                  <label
                    :for="`upgrade-${upgrade.type}`"
                    class="relative flex h-full w-full flex-col justify-between rounded-lg p-5 outline outline-depth-3 bg-depth-0 peer-checked:outline-brand peer-checked:text-brand overflow-hidden"
                    :class="{
                      'opacity-50 cursor-not-allowed': !upgrade.enabled,
                      'cursor-pointer hover:outline-brand': upgrade.enabled,
                    }"
                  >
                    <div class="block relative z-10">
                      <div
                        class="w-full flex items-center gap-4 text-lg font-semibold text-mouse"
                      >
                        <div>
                          <fa-icon
                            :icon="upgrade.icon"
                            class="size-10 icon-gradient"
                          />
                        </div>
                        <div>
                          <h4 class="mb-0 leading-tight">
                            {{ upgrade.title }}
                            <span
                              v-if="upgrade.type !== 'standard'"
                              class="text-rhino text-sm ms-1"
                            >
                              {{ upgrade.type === 'priority' ? '*' : '**' }}
                            </span>
                          </h4>

                          <span class="block font-light leading-tight">
                            {{ upgrade.price || '—' }}
                          </span>
                        </div>
                      </div>

                      <div
                        class="w-full text-rhino mt-2"
                        :class="
                          ticketForm.upgrade === upgrade.type
                            ? 'block'
                            : 'hidden md:block'
                        "
                      >
                        <!-- Display reason if addon is disabled and has a reason key -->
                        <p
                          v-if="!upgrade.enabled && upgrade.reasonKey"
                          class="text-rhino text-sm"
                        >
                          {{
                            $t(`support/addons/${upgrade.reasonKey}`, {
                              game: ticketForm.category?.name || '',
                            })
                          }}
                        </p>
                        <!-- Otherwise display subtitle and discount info when provided-->
                        <template v-else>
                          <p
                            v-if="upgrade.discountReasonKey"
                            class="text-rhino text-sm mt-1"
                          >
                            {{
                              $t(upgrade.discountReasonKey, {
                                count: upgrade.remainingFreeTickets,
                              })
                            }}
                          </p>
                          <p class="text-mouse">
                            {{ $t(`support/addons/${upgrade.type}_subtitle`) }}
                          </p>
                        </template>
                      </div>
                    </div>
                  </label>
                </li>
              </ul>

              <!-- Warning if no payment method. Suppressed when we could not
                   classify the customer at all (TV-1970): we do not know that
                   they need one, and canSubmitTicket lets them through. -->
              <div
                v-if="
                  !support.hasPaymentMethod &&
                  requiresPaymentMethod &&
                  !paymentMethodUnknown
                "
                class="flex items-center gap-2 p-4 mt-4 text-sm rounded-lg bg-warning/10 text-warning outline outline-warning"
                role="alert"
              >
                <fa-icon
                  :icon="faExclamationTriangle"
                  class="shrink-0 inline size-8 me-3"
                />
                <span class="sr-only">Warning</span>
                <div>
                  <span class="font-bold">
                    {{ $t('support/addons/payment_warning_title') }}
                  </span>
                  <div class="mt-1">
                    <i18n-t
                      keypath="support/addons/payment_warning_description"
                    >
                      <template #link>
                        <!-- Branch on the `payment-components` flag.
                             Flag on: open the inline Payment Components
                             modal. Flag off: fall back to the legacy
                             Chargebee portal URL (nightly behavior). -->
                        <button
                          v-if="paymentComponentsEnabled"
                          type="button"
                          class="text-warning font-bold underline cursor-pointer"
                          @click="openPaymentModal"
                        >
                          {{ $t('support/addons/payment_method_link') }}
                        </button>
                        <a
                          v-else
                          :href="support.paymentMethodUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-warning font-bold underline cursor-pointer"
                        >
                          {{ $t('support/addons/payment_method_link') }}
                        </a>
                      </template>
                    </i18n-t>
                  </div>
                </div>
              </div>

              <p
                v-if="balanceMigrated && requiresPaymentMethod"
                class="flex items-center gap-2 mt-4 text-sm text-rhino"
                data-testid="support-addon-balance-first"
              >
                <fa-icon
                  :icon="faInfoCircle"
                  class="size-4 shrink-0"
                  aria-hidden="true"
                />
                {{ $t('support/addons/balance_first') }}
              </p>
            </div>

            <!-- Terms -->
            <div v-if="requiresTerms" class="flex items-center mb-4">
              <input
                id="terms-checkbox"
                v-model="ticketForm.acceptedTerms"
                type="checkbox"
                class="w-4 h-4 text-brand rounded-sm"
                :aria-required="requiresTerms"
              />
              <label
                for="terms-checkbox"
                class="ms-2 text-sm font-medium text-rhino"
              >
                <i18n-t keypath="support/create_ticket/terms_label">
                  <template #tos>
                    <a
                      :href="termsUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="font-medium underline hover:no-underline"
                    >
                      {{ $t('support/create_ticket/terms_tos') }}
                    </a>
                  </template>
                  <template #privacy>
                    <a
                      :href="privacyUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="font-medium underline hover:no-underline"
                    >
                      {{ $t('support/create_ticket/terms_privacy') }}
                    </a>
                  </template>
                </i18n-t>
              </label>
            </div>

            <!-- Submit -->
            <common-button
              type="submit"
              size="sm"
              class="sm:w-fit"
              :disabled="!canSubmitTicket || isSubmitting"
            >
              {{
                isSubmitting
                  ? $t('support/create_ticket/sending')
                  : $t('support/create_ticket/send_ticket')
              }}
            </common-button>
          </form>

          <!-- Authenticated: table / detail -->
          <div v-else class="p-0">
            <tickets
              v-if="panel === 'table'"
              :items="tickets"
              :loading="isLoading"
              @row-click="onTicketRowClick"
              @write-ticket="handleWriteTicket"
            />

            <ticket-detail
              v-if="panel === 'detail' && activeTicket"
              :ticket="activeTicket"
              :messages="detailMessages"
              :loading="isDetailLoading"
              :enable-reply="activeTicket.status !== 'closed'"
              :send="onTicketSend"
              @close="onTicketBack"
            />

            <!-- `panel` comes from the URL, `activeTicket` from a fetch, so between the two there is
                 a state with neither a table nor a detail. Without this the authenticated branch
                 renders nothing at all — a blank card — for a ticket that is still loading, and
                 permanently for an id that is unknown, foreign or failed to load. -->
            <div
              v-else-if="panel === 'detail'"
              class="py-16 text-center text-rhino"
            >
              <template v-if="isDetailLoading">
                {{ $t('support/ticket_detail/loading') }}
              </template>
              <template v-else>
                <p>{{ $t('support/ticket_detail/not_found') }}</p>
                <common-button
                  variant="secondary"
                  outline
                  size="sm"
                  class="mt-4"
                  @click="onTicketBack"
                >
                  {{ $t('support/ticket_detail/back_to_tickets') }}
                </common-button>
              </template>
            </div>
          </div>
        </div>
        <!-- Additional info -->
        <div
          v-if="panel === 'write'"
          class="text-sm mt-16 mx-auto max-w-screen-lg"
        >
          <i18n-t
            keypath="support/addons/fastlane_footnote"
            tag="p"
            class="space-x-1"
          >
            <template #addon>
              <span class="text-rhino">*</span>
              <span class="font-bold">Fast Lane</span>
            </template>
          </i18n-t>
          <i18n-t
            keypath="support/addons/premium_footnote"
            tag="p"
            class="mt-4 space-x-1"
          >
            <template #addon>
              <span class="text-rhino">**</span>
              <span class="font-bold">Support+</span>
            </template>
          </i18n-t>
        </div>
      </div>
    </section>

    <!-- Payment method modal: renders chargebee Payment Components
         inline. Only mounted when the `payment-components` flag is on —
         off the modal would have no entry point and the chargebee.js
         publishable key needn't even be downloaded. -->
    <payment-method-modal
      v-if="paymentComponentsEnabled"
      :is-open="isPaymentModalOpen"
      @close="closePaymentModal"
      @success="onPaymentMethodAdded"
    />
  </main>
</template>

<script setup lang="ts">
import {
  faExclamationTriangle,
  faInfoCircle,
  faMagnifyingGlass,
} from '@fortawesome/pro-solid-svg-icons';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import CategorySelect from '~/components/support/CategorySelect.vue';
import GpInput from '~/components/support/GpInput.vue';
import GpSelect from '~/components/support/GpSelect.vue';
import PaymentMethodModal from '~/components/support/PaymentMethodModal.vue';
import TicketDetail from '~/components/support/TicketDetail.vue';
import Tickets from '~/components/support/Tickets.vue';
import useSearch from '~/composables/support/useSearch';
import useAuth from '~/composables/useAuth';
import useFeatureFlag, { FeatureFlag } from '~/composables/useFeatureFlag';
import { getLegacyLocalePrefix } from '~/helpers/legacyLocale';
import { useAuthStore } from '~/store/auth';
import { useSupportStore } from '~/store/support';
import { NAV_BUTTONS, UPGRADE_OPTIONS, WIKI_CARDS } from '~/store/support/data';
import type {
  AddonType,
  EnhancedAddon,
  Ticket,
  TicketAttachmentUpload,
  TicketCategory,
  UserServer,
} from '~/store/support/types';

// TODO: Enable once Strapi provides customizable knowledge/FAQ data
const KNOWLEDGE_SECTION_ENABLED = false;

// ---------------------------------------------------------------------------
// Stores & Auth
const authStore = useAuthStore();
const support = useSupportStore();
const { login } = useAuth();

// ---------------------------------------------------------------------------
// Payment Method Modal state. Only consulted when the
// `payment-components` flag is on; when off the warning text links to
// the legacy Chargebee portal URL instead (nightly behavior).
const { hasAccessToFeature } = useFeatureFlag();
const paymentComponentsEnabled = hasAccessToFeature(
  FeatureFlag.PaymentComponents,
);
const isPaymentModalOpen = ref(false);

const openPaymentModal = () => {
  isPaymentModalOpen.value = true;
};

const closePaymentModal = () => {
  isPaymentModalOpen.value = false;
};

const onPaymentMethodAdded = async () => {
  // Refresh support info to update hasPaymentMethod status
  await support.fetchSupportInfos();
};

const isAuth = computed<boolean>(() => authStore.isAuthenticated);
// Fast Lane and Support+ are paid from a migrated customer's balance first.
const { balanceMigrated } = useBalanceMigrated();

const { locale, t } = useI18n();

// ---------------------------------------------------------------------------
// Search functionality
const { state: searchState, hasResults: hasSearchResults } = useSearch();

// ---------------------------------------------------------------------------
// Data loading
const { pending: initialLoading } = useLazyAsyncData(
  'support:init',
  async () => {
    await support.fetchSupportInfos();
    if (authStore.isAuthenticated) {
      await support.fetchTickets();
    }
    return true;
  },
  { server: false },
);

// ---------------------------------------------------------------------------
// Computed state
const tickets = computed<Ticket[]>(() => support.ticketsList);
const isLoading = computed<boolean>(
  () => initialLoading.value || support.isLoading,
);
const isDetailLoading = computed<boolean>(() => support.isDetailLoading);

// ---------------------------------------------------------------------------
// Watchers (kept minimal; computed used elsewhere)
watch(
  () => authStore.isAuthenticated,
  async (isAuthenticated) => {
    if (isAuthenticated) {
      await support.fetchSupportInfos();
      if (tickets.value.length === 0) {
        await support.fetchTickets();
      }
    }
  },
);

watch(
  () => authStore.activeRegion,
  async () => {
    // Tickets are region-scoped, so the cached message threads are stale after a switch — and
    // fetchTickets deliberately no longer overwrites them (see the seeding comment in the store).
    // Clearing here is what keeps that guard safe. Not reset(), which would also drop the support
    // infos the write-ticket form needs.
    support.clearTicketMessages();
    await support.fetchTickets();
  },
);

// ---------------------------------------------------------------------------
// Locale & legal links
// Built from the locale/currency legacy prefix, exactly like the footer,
// checkout/TermsConsent.vue and the register form. Deliberately NOT
// useWebinterfaceUrl('/terms') — the ticket form is reachable logged out (a
// guest picking a paid support addon sees the consent links), and logged out
// that helper returns `/${region.value}/...` where region is only ever 'eur'
// or 'int', so the link 404s (same root cause as TV-1941).
const currencyStore = useCurrencyStore();
const termsUrl = computed(
  () => `/${getLegacyLocalePrefix(locale.value, currencyStore.currency)}/terms`,
);
const privacyUrl = computed(
  () =>
    `/${getLegacyLocalePrefix(locale.value, currencyStore.currency)}/privacy`,
);

const supportOptionsWikiUrl = computed<string>(() => {
  const lang = (locale.value || '').toLowerCase();
  return lang.startsWith('de')
    ? 'https://www.g-portal.com/wiki/de/uebersicht-der-gportal-support-optionen/'
    : 'https://www.g-portal.com/wiki/en/overview-of-the-gportal-support-options/';
});

// ---------------------------------------------------------------------------
// Panel state
const panel = ref<'write' | 'table' | 'detail'>(
  isAuth.value ? 'table' : 'write',
);

// ---------------------------------------------------------------------------
// Ticket form state
type TicketForm = {
  email: string;
  category: TicketCategory | null;
  server: UserServer | null;
  title: string;
  message: string;
  upgrade: AddonType;
  acceptedTerms: boolean;
};

const ticketForm = reactive<TicketForm>({
  email: '',
  category: null,
  server: null,
  title: '',
  message: '',
  upgrade: 'standard',
  acceptedTerms: false,
});

// Draft persistence uses sessionStorage rather than cookies. The ticket
// subject + body routinely contain emails, order ids, refund references
// and occasionally pasted credentials — keeping them in a cookie would
// attach them to every request to g-portal.com (nginx access logs, CDN
// edge logs, SIEM, DevTools) for no benefit (GDPR art. 5 minimisation).
// sessionStorage is per-tab and never sent over the wire, while still
// surviving the page navigation through the payment-method modal that
// the persistence is here to cover.
// Namespace the draft per signed-in account so that logging out and signing in
// as a different user in the same tab can't restore the previous user's draft
// into the new account's form.
function draftStorageKey(): string {
  return `support_ticket_draft:${authStore.user?.id ?? 'anon'}`;
}

type TicketDraft = {
  title?: string;
  message?: string;
  upgrade?: AddonType;
  category?: string;
};

function readDraft(): TicketDraft {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(draftStorageKey());
    return raw ? (JSON.parse(raw) as TicketDraft) : {};
  } catch {
    return {};
  }
}

function writeDraft(draft: TicketDraft) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(draftStorageKey(), JSON.stringify(draft));
  } catch {
    // ignore — quota errors or disabled storage shouldn't break the form.
  }
}

function clearDraft() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(draftStorageKey());
  } catch {
    // ignore.
  }
}

const allowedAddons: AddonType[] = ['standard', 'priority', 'premium'];

// Hydrate from sessionStorage on first mount. Wrapped in onMounted so
// SSR doesn't see different state than the initial client render.
onMounted(() => {
  const draft = readDraft();
  if (draft.title) ticketForm.title = draft.title;
  if (draft.message) ticketForm.message = draft.message;
  if (draft.upgrade && allowedAddons.includes(draft.upgrade)) {
    ticketForm.upgrade = draft.upgrade;
  }
});

// Single watcher persists every relevant field together so we only touch
// sessionStorage once per change.
watch(
  () => ({
    title: ticketForm.title,
    message: ticketForm.message,
    upgrade: ticketForm.upgrade,
    category: ticketForm.category?.id ? String(ticketForm.category.id) : '',
  }),
  (snap) => {
    writeDraft(snap);
  },
);

const categoryMap = computed(() => {
  // Guards against `support.gameCategories`/`uncategorizedCategories` reading as undefined —
  // seen in production as "a.gameCategories is not iterable" — when this computed's eager
  // evaluation (watch() below reads it once during setup()) races a stale/duplicate Pinia
  // store instance rather than the fresh, always-array state this store's own code writes.
  const all = [
    ...(support.gameCategories ?? []),
    ...(support.uncategorizedCategories ?? []),
  ];
  return new Map(all.map((c) => [String(c.id), c]));
});

// One-shot hydration: once categoryMap has populated, restore the
// stored category exactly once and stop watching. A live watchEffect
// would re-run whenever categoryMap recomputes (e.g. after a
// fetchSupportInfos refetch triggered by onPaymentMethodAdded), which
// would silently restore the draft value if the user had cleared their
// selection in the meantime.
// `categoryHydrated` is the one-shot guard: restore the stored category the
// first time categoryMap is populated and never again, so a later
// fetchSupportInfos refetch (e.g. after onPaymentMethodAdded) can't silently
// re-restore a selection the user has since cleared. The flag — not a
// self-stopping handle — enforces "once": with { immediate: true } Vue runs
// this callback synchronously inside watch(), so calling a `const` stop
// handle from here would hit its temporal dead zone. The watcher itself is
// bound to this setup scope and is disposed automatically on unmount; after
// hydration every run is a cheap early-return.
let categoryHydrated = false;
watch(
  categoryMap,
  (map) => {
    if (categoryHydrated || map.size === 0) return;
    categoryHydrated = true;
    if (ticketForm.category) return;
    const draftCategory = readDraft().category;
    if (!draftCategory) return;
    const cat = map.get(draftCategory);
    if (cat) {
      ticketForm.category = cat;
    }
  },
  { immediate: true },
);

// Keep an active selection pointing at the live category object across
// refetches. fetchSupportInfos (e.g. after adding a payment method from the
// warning modal) rebuilds category objects with fresh identities, and
// CategorySelect matches the selected option by reference — so a stale
// reference would render as "nothing selected" even though a category is
// chosen. Re-point to the refreshed object with the same id. Guarded on an
// existing selection, so it never re-restores one the user cleared, and the
// id is unchanged so the category-change watcher (which resets server /
// upgrade) doesn't fire.
watch(categoryMap, (map) => {
  const current = ticketForm.category;
  if (!current) return;
  const refreshed = map.get(String(current.id));
  if (refreshed && refreshed !== current) {
    ticketForm.category = refreshed;
  }
});

const isSubmitting = ref(false);
const ticketError = ref('');

const requiresTerms = computed<boolean>(
  () => ticketForm.upgrade !== 'standard',
);

// Check if selected category is a game category
const isGameSelected = computed<boolean>(() => {
  const selectedId = ticketForm.category?.id;
  if (!selectedId) return false;
  return (support.gameCategories ?? []).some((c) => c.id === selectedId);
});

// Check if selected category is uncategorized
const isUncategorizedSelected = computed<boolean>(() => {
  const selectedId = ticketForm.category?.id;
  if (!selectedId) return false;
  return (support.uncategorizedCategories ?? []).some(
    (c) => c.id === selectedId,
  );
});

// Determine if we should show server selection
const showServer = computed<boolean>(() => {
  // Only show server selection for game categories
  return isGameSelected.value;
});

// Determine if we should show addons section
const showAddons = computed<boolean>(() => {
  // If no category selected, don't show addons
  if (!ticketForm.category) return false;

  // If uncategorized selected, don't show addons
  if (isUncategorizedSelected.value) return false;

  // For game categories, check if there are any addons in the array
  const category = ticketForm.category;
  // Only show addons section if there are addons available (other than standard)
  return category.addons.length > 0;
});

// The currently selected upgrade option, with its Loot Pass free-ticket
// and pricing metadata.
const selectedUpgrade = computed<EnhancedAddon | undefined>(() =>
  filteredUpgradeOptions.value.find(
    (option) => option.type === ticketForm.upgrade,
  ),
);

// Whether an add-on is charged, so it needs a payment method. Fast Lane
// (priority) and Support+ (premium) are paid add-ons, but Fast Lane is
// covered while the user still has Loot Pass free tickets
// (remainingFreeTickets > 0); only once those run out does it require
// payment. Support+ has no free tickets, so it always requires one.
const isChargeableAddon = (upgrade: EnhancedAddon): boolean =>
  upgrade.type === 'premium' ||
  (upgrade.type === 'priority' && (upgrade.remainingFreeTickets ?? 0) <= 0);

const requiresPaymentMethod = computed<boolean>(
  () =>
    showAddons.value &&
    selectedUpgrade.value != null &&
    isChargeableAddon(selectedUpgrade.value),
);

// An empty paymentMethodUrl alongside hasPaymentMethod === false means we could not classify the
// customer at all (incomplete profile, or a Chargebee failure such as the 429 rate limit) - it
// does not mean they lack a payment method. buildPaymentMethodInfo only returns false with an
// empty url in that unclassifiable case; a genuine "no payment method" still carries a url.
const paymentMethodUnknown = computed<boolean>(
  () => !support.hasPaymentMethod && support.paymentMethodUrl.length === 0,
);

const canSubmitTicket = computed<boolean>(() => {
  const hasRequiredFields = !!ticketForm.title && !!ticketForm.message;

  const hasAcceptedTerms = !requiresTerms.value || ticketForm.acceptedTerms;

  // Don't block on hasPaymentMethod when we simply failed to classify the customer - Chargebee's
  // payment authorization at ticket creation is the authority and will fail loudly with a real
  // reason if the customer truly has no method.
  return (
    hasRequiredFields &&
    hasAcceptedTerms &&
    (!requiresPaymentMethod.value ||
      support.hasPaymentMethod ||
      paymentMethodUnknown.value)
  );
});

// Filter servers based on the selected category
const filteredServers = computed<UserServer[]>(() => {
  if (!ticketForm.category) return [];
  return ticketForm.category.customerServers || [];
});

// Filter upgrade options based on selected category
const filteredUpgradeOptions = computed<EnhancedAddon[]>(() => {
  return support.buildUpgradeOptions(UPGRADE_OPTIONS, ticketForm.category);
});

// ---------------------------------------------------------------------------

// Reset server when category changes
watch(
  () => ticketForm.category?.id,
  () => {
    ticketForm.server = null;

    // Reset upgrade to standard if category doesn't support addons
    if (!showAddons.value || isUncategorizedSelected.value) {
      ticketForm.upgrade = 'standard';
    }
  },
);

// ---------------------------------------------------------------------------
// Helper functions
const scrollToTickets = (): void => {
  nextTick(() => {
    document.getElementById('tickets')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  });
};

// ---------------------------------------------------------------------------
// Ticket form actions
const submitTicket = async (): Promise<void> => {
  isSubmitting.value = true;
  ticketError.value = '';

  try {
    const categoryId = ticketForm.category?.id;
    const serverId = ticketForm.server?.id;

    // Prepare addon information if applicable
    const addonType =
      showAddons.value && ticketForm.upgrade !== 'standard'
        ? ticketForm.upgrade
        : undefined;

    const result = await support.createTicket(
      ticketForm.title,
      ticketForm.message,
      categoryId,
      serverId,
      addonType,
    );

    if (result.status === 'success') {
      // Drop the sessionStorage draft on successful submit so a fresh
      // ticket on the same tab starts from a clean form.
      clearDraft();
      // Reset form (immutable reset)
      Object.assign(ticketForm, {
        email: '',
        category: null,
        server: null,
        title: '',
        message: '',
        upgrade: 'standard',
        acceptedTerms: false,
      });

      // Open the newly created ticket. The store already inserted it (and its first message)
      // optimistically, so we deliberately do NOT refetch the list here: the Intercom conversation
      // search index lags a few seconds, and a refetch would momentarily drop the just-created
      // ticket from the list.
      if (result.ticket) {
        openTicket(result.ticket.id);
      } else {
        panel.value = 'table';
      }
      scrollToTickets();
    } else {
      const statusKey = result.status.replaceAll('-', '_');
      ticketError.value =
        t?.(`support/error/create_ticket_${statusKey}`)?.toString?.() ||
        t?.('support/error/create_ticket')?.toString?.() ||
        'Failed to create ticket. Please try again.';
    }
  } catch (error) {
    console.error('Error creating ticket:', error);
    ticketError.value = 'An error occurred. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

// ---------------------------------------------------------------------------
// Ticket detail state
//
// The open ticket is part of the URL (`/support/<ticket-id>`) rather than local state, so a ticket
// can be linked, bookmarked and pasted to a colleague, and so browser Back returns to the list
// instead of leaving the support page entirely. `activeTicketId` follows the route rather than the
// click; every "open this ticket" path below navigates, and this watcher reacts.
const route = useRoute();
const localePath = useLocalePath();

const routeTicketId = computed<string | null>(() => {
  const param = route.params.ticketId;
  return typeof param === 'string' && param.length > 0 ? param : null;
});

const activeTicketId = ref<string | null>(null);

// Ticket ids are passed through as-is, colon included: `intercom:215561148658607`. vue-router's
// param encoder is built on encodeURI, which leaves `:` alone (RFC 3986 permits it in a path
// segment), so the URL stays readable instead of becoming `intercom%3A215...`.
const openTicket = (id: string): void => {
  void navigateTo(localePath(`/support/${id}`));
};

const closeTicket = (): void => {
  void navigateTo(localePath('/support'));
};

// `?compose=1`, because leaving a ticket means changing route, and `/support/[ticketId]` and
// `/support` are different page components — the `<support />` instance that set `panel` is
// destroyed by the navigation and the new one mounts back at its default. Local state cannot
// survive that, so the intent travels in the URL, which also makes the compose form linkable.
const composeRequested = computed<boolean>(() => route.query.compose === '1');

const openCompose = (): void => {
  void navigateTo(localePath({ path: '/support', query: { compose: '1' } }));
};

watch(
  routeTicketId,
  async (id) => {
    if (!id) {
      activeTicketId.value = null;
      if (panel.value === 'detail') {
        panel.value = isAuth.value ? 'table' : 'write';
      }
      return;
    }

    activeTicketId.value = id;
    panel.value = 'detail';

    // Client-only and authenticated-only. The watcher is immediate, so without the first guard it
    // would fire during server rendering; without the second, a signed-out visitor opening a ticket
    // URL gets a failed fetch and an error toast sitting next to the login prompt they were always
    // going to see. The retry below picks it up once they sign in.
    if (import.meta.client && isAuth.value) {
      // Also covers a cold load straight onto a ticket that is not in the fetched page of the list:
      // the store inserts what it fetches, so the detail resolves either way.
      await support.fetchTicketById(id);
    }
  },
  { immediate: true },
);

// Restores the compose panel after the navigation that carried the request, and on a cold load of
// a `?compose=1` link.
watch(
  composeRequested,
  (compose) => {
    if (compose) {
      panel.value = 'write';
      return;
    }

    // And reverse it. Browser Back from `?compose=1` to `/support` would otherwise leave the form up
    // while the URL no longer asks for it — and reloading that same URL then shows the list, so one
    // address renders two different things depending on how you arrived.
    //
    // Only from `write`, and only when signed in on the list route: `write` is the correct default
    // for a signed-out visitor, and the ticket route owns the panel while a ticket is open.
    if (panel.value === 'write' && isAuth.value && !routeTicketId.value) {
      panel.value = 'table';
    }
  },
  { immediate: true },
);

// The counterpart to that auth guard. A cold load on a ticket URL while signed out skips its detail
// fetch, and nothing else re-triggers it — the auth watcher above only refetches the LIST, which for
// an Intercom ticket carries just the opening message.
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated && routeTicketId.value) {
      void support.fetchTicketById(routeTicketId.value);
    }
  },
);

const activeTicket = computed<Ticket | null>(
  () =>
    tickets.value.find((ticket) => ticket.id === activeTicketId.value) ?? null,
);

const detailMessages = computed(() =>
  activeTicketId.value ? support.getMessages(activeTicketId.value) : [],
);

// ---------------------------------------------------------------------------
// Ticket detail actions
// Returns whether the reply landed, so TicketDetail can keep the draft and the picked files when it
// did not — an awaited callback rather than an event for exactly that reason. It also owns the
// in-flight guard, since the await here is what that guard has to span.
const onTicketSend = async (
  body: string,
  attachments: TicketAttachmentUpload[] = [],
): Promise<boolean> => {
  if (!activeTicketId.value) return false;

  // sendReply already updates the ticket and its messages in the store; we deliberately don't
  // refetch the whole list here, which would momentarily drop a just-created ticket (Intercom
  // search lag) and collapse the open detail view.
  return support.sendReply(activeTicketId.value, body, attachments);
};

const onTicketRowClick = (row: { id: string }): void => {
  openTicket(row.id);
  scrollToTickets();
};

const onTicketBack = (): void => {
  closeTicket();
};

const handleWriteTicket = (): void => {
  // Always via the route. From a ticket this is a real navigation; from the list it only adds the
  // query, which the watcher below turns into the panel change. One path, so the URL and the panel
  // cannot disagree.
  openCompose();
  panel.value = 'write';
  scrollToTickets();
};

// ---------------------------------------------------------------------------
// Navigation actions
const handleCta = (btn: (typeof NAV_BUTTONS)[number]): void => {
  if (btn.kind === 'external' && btn.href) {
    window.open(btn.href, '_blank');
    return;
  }

  if (btn.key === 'write_ticket') {
    handleWriteTicket();
    return;
  }

  if (btn.key === 'my_tickets') {
    if (!isAuth.value) {
      login();
      return;
    }
    if (routeTicketId.value || composeRequested.value) {
      closeTicket();
    }
    panel.value = 'table';
    if (tickets.value.length === 0) {
      support.fetchTickets();
    }
    scrollToTickets();
  }
};

// ---------------------------------------------------------------------------
// Wiki state (currently hidden - v-if="false")
const selectedWiki = ref<string>(WIKI_CARDS[0]?.title || '');
const selectedWikiCard = computed(
  () => WIKI_CARDS.find((c) => c.title === selectedWiki.value) || null,
);
const selectedWikiLinks = computed(() => selectedWikiCard.value?.links ?? []);
</script>
